import { Component, OnInit, inject, input } from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';

import { MessagesService } from '../../services/messages/messages.service';
import { slideInOutAnimation } from '../../utils/animation/animation';
import { Message } from '../../domain/messages.domain';
import { EmptyComponent } from '../../components/datatable/components/empty/empty.component';
import { EmptyHeaderComponent } from '../../components/datatable/components/empty/empty-header.component';
import { IconComponent } from '../../components/icon/icon.component';
import { ButtonComponent } from '../../components/button/button.component';
import { ExpandIconComponent } from '../../components/expand-icon/expand-icon.component';
import { DatePipe } from '../../pipes/date.pipe';
import { HtmlSanitizerService } from '../../utils/sanitization/strip-html-helper';
import { MessagesBodyComponent } from './messages-body.component';

export interface DisplayMessage extends Message {
  teaser: string;
  expanded: boolean;
}

@Component({
  selector: 'td-wb-messages',
  templateUrl: 'messages.component.html',
  styleUrls: ['messages.component.scss'],
  animations: [slideInOutAnimation],
  imports: [
    TranslateDirective,
    EmptyComponent,
    EmptyHeaderComponent,
    IconComponent,
    ButtonComponent,
    ExpandIconComponent,
    MessagesBodyComponent,
    DatePipe
  ]
})
export class MessagesComponent implements OnInit {
  private readonly messagesService = inject(MessagesService);
  private readonly htmlSanitizerService = inject(HtmlSanitizerService);

  readonly messages = input<Array<DisplayMessage>>(undefined);
  readonly expandMessages = input<boolean>(undefined);
  readonly updateMessageCounter = input<boolean>(undefined);

  ngOnInit(): void {
    this.processMessages();
    if (this.updateMessageCounter()) {
      this.markAsRead();
    }
  }

  isMessages(): boolean {
    const messages = this.messages();
    return messages !== undefined && messages instanceof Array && messages.length > 0;
  }

  getMessageClass(message: DisplayMessage): string {
    const messageClass = 'td-wb-fixed-messages__item';
    if (message !== undefined && message.expanded) {
      return messageClass + ' td-wb-fixed-messages__item--expanded';
    }
    return messageClass;
  }

  toggle(message: DisplayMessage): boolean {
    message.expanded = !message.expanded;
    return false;
  }

  private processMessages(): void {
    const messages = this.messages();
    if (messages !== undefined && messages instanceof Array) {
      messages.sort(this.messageSort);
      messages.forEach((message: DisplayMessage): void => {
        message.teaser = this.htmlSanitizerService.stripHtml(message.body);
        if (this.expandMessages()) {
          message.expanded = true;
        }
      });
    }
  }

  private messageSort(a: DisplayMessage, b: DisplayMessage): number {
    if (a.importance < b.importance) {
      return -1;
    } else if (a.importance > b.importance) {
      return 1;
    } else {
      // importance is equivalent, so sort by reverse effective date
      if (a.effectiveDate < b.effectiveDate) {
        return 1;
      } else if (a.effectiveDate > b.effectiveDate) {
        return -1;
      } else {
        return 0;
      }
    }
  }

  private markAsRead(): void {
    this.messagesService.markMessagesAsRead(this.messages(), true);
  }
}
